import React, {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  useEffect,
} from "react";
import ReactFlow, { ReactFlowProvider, addEdge, Controls } from "reactflow";
import "reactflow/dist/style.css";
import { applyNodeChanges } from "reactflow";
import { useParams, useHistory } from "react-router-dom";
import RightPanelRenderer from "./RightPanelRenderer";
import "./RightPanelRenderer.css";
import useCreditPlanFlowNodes from "./useCreditPlanFlowNodes";
import {
  getCreditPlan,
  getAssessmentById,
} from "../../services/assessmentService";
import { useTranslation } from "../../hooks/useTranslation";
import { useScope } from "../../hooks/useScope";
import MarkdownRenderer from "./MarkdownRenderer";
import ProgressIndicator from "./ProgressIndicator";

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Credit plan data will be loaded dynamically from assessment data or plan ID

const ROOT_ID = "start";

// Simple vertical node for top-to-bottom orientation
import { Handle, Position } from "reactflow";

function VerticalNode({ data }) {
  const handleClick = () => {
    if (data.onHeaderClick) {
      data.onHeaderClick(data.id);
    }
  };

  // Get node styling based on status
  const getNodeStyling = () => {
    const status = data.status || "standby";
    const isSelected = data.isSelected || false;

    // Neon colors for selected state
    const neonBorder = "3px solid #00ffff"; // Cyan neon
    const neonBoxShadow =
      "0 0 10px #00ffff, 0 0 20px #00ffff, 0 0 30px #00ffff";

    let baseStyle;
    switch (status) {
      case "standby":
        baseStyle = {
          background: "#fff",
          border: "12px solid #95a5a6",
          color: "#95a5a6",
          handleColor: "#95a5a6",
        };
        break;
      case "in-progress":
        baseStyle = {
          background: "#fff",
          border: "12px solid #f39c12",
          color: "#f39c12",
          handleColor: "#f39c12",
        };
        break;
      case "done":
        baseStyle = {
          background: "#fff",
          border: "12px solid #27ae60",
          color: "#27ae60",
          handleColor: "#27ae60",
        };
        break;
      default:
        baseStyle = {
          background: "#fff",
          border: "2px solid #1976d2",
          color: "#1976d2",
          handleColor: "#1976d2",
        };
    }

    // If selected, override border with neon effect
    if (isSelected) {
      return {
        ...baseStyle,
        border: neonBorder,
        boxShadow: neonBoxShadow,
        outline: "none",
      };
    }

    return baseStyle;
  };

  const nodeStyle = getNodeStyling();

  return (
    <div
      onClick={handleClick}
      style={{
        minWidth: 180,
        maxWidth: 600,
        minHeight: 60,
        background: nodeStyle.background,
        border: nodeStyle.border,
        borderRadius: 8,
        boxShadow: nodeStyle.boxShadow || "0 2px 8px rgba(0,0,0,0.07)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 12,
        fontWeight: 500,
        color: nodeStyle.color,
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        // Use currentTarget to always reference the outer div, not child elements
        const nodeElement = e.currentTarget;
        nodeElement.style.transform = "scale(1.02)";
        // Preserve neon glow if selected, otherwise use default hover shadow
        if (data.isSelected) {
          // Enhanced neon glow on hover
          nodeElement.style.boxShadow =
            "0 0 15px #00ffff, 0 0 25px #00ffff, 0 0 35px #00ffff";
        } else {
          nodeElement.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
        }
      }}
      onMouseLeave={(e) => {
        // Use currentTarget to always reference the outer div, not child elements
        const nodeElement = e.currentTarget;
        nodeElement.style.transform = "scale(1)";
        // Restore neon glow if selected, otherwise use default shadow
        if (data.isSelected) {
          nodeElement.style.boxShadow = nodeStyle.boxShadow;
        } else {
          nodeElement.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)";
        }
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: nodeStyle.handleColor }}
      />
      <div style={{ fontSize: 25, marginBottom: data.details ? 4 : 0 }}>
        {data.label}
      </div>
      {data.details && (
        <div
          style={{
            fontSize: 20,
            color: "#333",
            marginTop: 2,
            maxWidth: "500px",
            textAlign: "center",
            marginTop: "5px",
          }}
        >
          {data.details.length > 100
            ? data.details.substring(0, 100) + "..."
            : data.details}
        </div>
      )}
      {/* Deadline bar: only render if deadline exists */}
      {!!data.deadline &&
        (() => {
          const now = new Date();
          const deadlineDate = new Date(data.deadline);
          const daysRemaining = (deadlineDate - now) / (1000 * 60 * 60 * 24);
          let barColor = "#27ae60"; // green
          if (daysRemaining < 0) barColor = "#e74c3c"; // red
          else if (daysRemaining <= 3) barColor = "#e74c3c"; // red
          else if (daysRemaining <= 7) barColor = "#f39c12"; // orange
          else if (daysRemaining <= 14) barColor = "#f1c40f"; // yellow
          return (
            <div
              style={{
                width: "90%",
                height: 7,
                borderRadius: 4,
                margin: "8px auto 0 auto",
                background: barColor,
                transition: "background 0.3s",
              }}
            />
          );
        })()}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: nodeStyle.handleColor }}
      />
    </div>
  );
}

const nodeTypes = { expandable: VerticalNode };

// Error Boundary for ReactFlow
class ReactFlowErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Suppress ResizeObserver errors - check multiple ways the error might be represented
    const errorMessage = error?.message || error?.toString() || "";
    const errorString = typeof error === "string" ? error : errorMessage;

    if (
      errorString.includes("ResizeObserver") ||
      errorString.includes("ResizeObserver loop")
    ) {
      return { hasError: false };
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Suppress ResizeObserver errors - check multiple ways the error might be represented
    const errorMessage = error?.message || error?.toString() || "";
    const errorString = typeof error === "string" ? error : errorMessage;

    if (
      errorString.includes("ResizeObserver") ||
      errorString.includes("ResizeObserver loop")
    ) {
      // Silently ignore ResizeObserver errors
      return;
    }
    console.error("ReactFlow Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Note: Translation not available in error boundary, using static text
      return <div>Something went wrong with the flow diagram.</div>;
    }
    return this.props.children;
  }
}

export default function CreditPlanFlow({
  assessmentData = null,
  planId = null,
  isDemo = false,
}) {
  const wrapperRef = useRef(null);
  const t = useTranslation();
  const history = useHistory();
  const { planId: urlPlanId } = useParams(); // Read planId from URL params
  const { isClientView, isManaged, mentorId, effectiveUserId, actualUserId } = useScope();
  const [rfInstance, setRfInstance] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tree, setTree] = useState({});
  const [loading, setLoading] = useState(false);
  const [regeneratingPlan, setRegeneratingPlan] = useState(false);
  const [currentAssessmentData, setCurrentAssessmentData] =
    useState(assessmentData);
  const [currentPlanId, setCurrentPlanId] = useState(planId);
  const [showAIPreview, setShowAIPreview] = useState(false);
  const hasInitialFitView = useRef(false);
  const previousTreeKeys = useRef(null);

  // Load credit plan data if assessmentData or planId is provided
  useEffect(() => {
    const loadCreditPlan = async () => {
      let dataToUse = assessmentData;
      // Prioritize URL planId over props
      const planIdToUse = urlPlanId || planId;

      // If URL planId exists, load directly from database
      if (urlPlanId && !isDemo) {
        setLoading(true);
        try {
          console.log("Loading credit plan from URL planId:", urlPlanId);
          const result = await getCreditPlan(urlPlanId);
          if (result?.planStructure) {
            // Scope verification: check if plan matches current scope
            const planMentorId = result.mentorId;
            const planUserId = result.userId;
            const isPersonalPlan = !planMentorId;
            const isMentorPlan = !!planMentorId;
            
            // Determine if we're in mentor masquerade mode
            const isMentorMasquerade = isManaged && !isClientView;

            // Mentor masquerade mode: allow if plan belongs to client (effectiveUserId) 
            // and was created by this mentor (actualUserId matches planMentorId)
            if (isMentorMasquerade) {
              const planBelongsToClient = planUserId === effectiveUserId;
              const planCreatedByMentor = planMentorId === actualUserId;
              
              if (!planBelongsToClient || !planCreatedByMentor) {
                console.log(
                  "Blocking access: mentor masquerade - plan doesn't belong to client or wasn't created by this mentor",
                  { planUserId, effectiveUserId, planMentorId, actualUserId }
                );
                history.replace("/history");
                return;
              }
            }
            // Personal view (not in any managed scope): block mentor plans
            else if (!isManaged && isMentorPlan) {
              console.log(
                "Blocking access: personal view trying to access mentor plan"
              );
              history.replace("/history");
              return;
            }
            // Client view: must match mentor and be a mentor plan
            else if (isClientView) {
              if (isPersonalPlan) {
                console.log(
                  "Blocking access: client view trying to access personal plan"
                );
                history.replace("/history");
                return;
              }
              if (mentorId && planMentorId !== mentorId) {
                console.log(
                  "Blocking access: client view trying to access plan from different mentor"
                );
                history.replace("/history");
                return;
              }
            }

            setCurrentPlanId(urlPlanId);
            const newTreeKeys = Object.keys(result.planStructure)
              .sort()
              .join(",");
            if (previousTreeKeys.current !== newTreeKeys) {
              hasInitialFitView.current = false;
            }
            setTree(result.planStructure);
            // Set assessment data if available from plan
            if (result.userId && result.goalId) {
              // Fetch the assessment document to get AI analysis
              // Assessment ID format is the same as planId: ${userId}_${goalId}
              let aiAnalysis = null;
              try {
                const assessmentResult = await getAssessmentById(urlPlanId);
                if (
                  assessmentResult?.success &&
                  assessmentResult?.data?.aiAnalysis
                ) {
                  aiAnalysis = assessmentResult.data.aiAnalysis;
                  console.log(
                    "Successfully loaded AI analysis from assessment"
                  );
                } else {
                  console.log("No AI analysis found in assessment document");
                }
              } catch (assessmentError) {
                console.warn(
                  "Could not load assessment for AI analysis:",
                  assessmentError
                );
                // Continue without AI analysis - it's not critical
              }

              setCurrentAssessmentData({
                userId: result.userId,
                goalId: result.goalId,
                goal: result.goal,
                subcategory: result.subcategory,
                aiAnalysis: aiAnalysis,
                creditPlan: result,
              });
            }
          } else {
            console.log("No plan structure found for URL planId:", urlPlanId);
            setTree({});
            hasInitialFitView.current = false;
            previousTreeKeys.current = null;
          }
        } catch (error) {
          console.error("Error loading credit plan from URL:", error);
          setTree({});
        } finally {
          setLoading(false);
          setRegeneratingPlan(false);
        }
        return; // Exit early when loading from URL
      }

      // If planId is provided as prop, use it directly
      if (planId) {
        setCurrentPlanId(planId);
      }

      // If no props provided, try to get data from sessionStorage or localStorage (backward compatibility)
      if (!assessmentData && !planId && !urlPlanId) {
        // First check sessionStorage for assessment data
        const storedData = sessionStorage.getItem("assessmentData");
        if (storedData) {
          try {
            dataToUse = JSON.parse(storedData);
            setCurrentAssessmentData(dataToUse);
            // Clear the stored data after reading it
            sessionStorage.removeItem("assessmentData");
          } catch (error) {
            console.error("Error parsing stored assessment data:", error);
          }
        } else {
          // Check localStorage for generated plan data
          const generatedPlanData = localStorage.getItem("generatedPlanData");
          if (generatedPlanData) {
            try {
              const parsedPlanData = JSON.parse(generatedPlanData);
              dataToUse = { creditPlan: parsedPlanData };
              setCurrentAssessmentData(dataToUse);
              // Clear the stored data after reading it
              localStorage.removeItem("generatedPlanData");
            } catch (error) {
              console.error("Error parsing generated plan data:", error);
            }
          }
        }
      } else {
        setCurrentAssessmentData(assessmentData);
      }

      // If we have assessment data but no AI analysis, try to fetch the full assessment
      // Skip this in demo mode to avoid Firestore calls
      if (!isDemo && dataToUse && !dataToUse.aiAnalysis && dataToUse.id) {
        try {
          console.log("Fetching full assessment data for ID:", dataToUse.id);
          const fullAssessment = await getAssessmentById(dataToUse.id);
          if (fullAssessment.success && fullAssessment.data) {
            dataToUse = { ...dataToUse, ...fullAssessment.data };
            setCurrentAssessmentData(dataToUse);
          }
        } catch (error) {
          console.error("Error fetching full assessment data:", error);
        }
      }

      if (!dataToUse && !planIdToUse) {
        // If no data is available, show empty state
        setTree({});
        hasInitialFitView.current = false;
        previousTreeKeys.current = null;
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let planData;

        if (planIdToUse && !isDemo) {
          // Load existing credit plan (skip in demo mode)
          const result = await getCreditPlan(planIdToUse);
          planData = result;
        } else if (dataToUse?.creditPlan) {
          // Use credit plan from assessment data
          planData = dataToUse.creditPlan;
          // Extract plan ID from the assessment data
          if (dataToUse.userId && dataToUse.goalId) {
            const extractedPlanId = `${dataToUse.userId}_${dataToUse.goalId}`;
            console.log(
              "Extracted plan ID from assessment data:",
              extractedPlanId
            );
            setCurrentPlanId(extractedPlanId);
          }
        } else if (dataToUse?.customGoalData && !isDemo) {
          // Handle custom goal - load existing plan from Firebase (skip in demo mode)
          console.log(
            "Loading custom goal plan from Firebase:",
            dataToUse.customGoalData
          );

          try {
            // First, try to find the custom goal assessment to get the correct goalId
            const { getAssessmentsByGoal } = await import(
              "../../services/assessmentService"
            );
            const assessmentsResult = await getAssessmentsByGoal({
              subcategory: dataToUse.customGoalData.subcategory,
            });

            // Find the most recent custom goal assessment
            const customGoalAssessment =
              assessmentsResult.data?.assessments?.find(
                (assessment) =>
                  assessment.subcategory === "custom_goal" ||
                  assessment.form_type === "custom_goal_form"
              );

            if (customGoalAssessment) {
              // Use the goalId from the assessment to construct the plan ID
              const constructedPlanId = `${customGoalAssessment.userId}_${customGoalAssessment.goalId}`;
              console.log(
                "Found custom goal assessment, trying to load plan with ID:",
                constructedPlanId
              );

              // Set the current plan ID
              setCurrentPlanId(constructedPlanId);

              // Load the existing plan from Firebase
              const result = await getCreditPlan(constructedPlanId);
              planData = result;
              console.log(
                "Successfully loaded existing custom goal plan from Firebase"
              );
            } else {
              throw new Error("No custom goal assessment found");
            }
          } catch (error) {
            console.error("Error loading custom goal plan:", error);
            // If we can't load the plan, show an error or fallback
            throw new Error("Failed to load custom goal plan from Firebase");
          }
        }

        if (planData?.planStructure) {
          console.log(
            "Setting tree with plan structure:",
            planData.planStructure
          );
          console.log("Tree keys:", Object.keys(planData.planStructure));
          // Reset fitView flag when loading a new plan
          const newTreeKeys = Object.keys(planData.planStructure)
            .sort()
            .join(",");
          if (previousTreeKeys.current !== newTreeKeys) {
            hasInitialFitView.current = false;
          }
          setTree(planData.planStructure);

          // If we still don't have a plan ID, try to construct it from the plan data
          if (!currentPlanId && planData.userId && planData.goalId) {
            const constructedPlanId = `${planData.userId}_${planData.goalId}`;
            console.log(
              "Constructing plan ID from plan data:",
              constructedPlanId
            );
            setCurrentPlanId(constructedPlanId);
          }
        } else {
          console.log("No plan structure found, setting empty tree");
          setTree({});
          hasInitialFitView.current = false;
          previousTreeKeys.current = null;
        }
      } catch (error) {
        console.error("Error loading credit plan:", error);
        setTree({});
      } finally {
        setLoading(false);
        setRegeneratingPlan(false);
      }
    };

    loadCreditPlan();
  }, [assessmentData, planId, urlPlanId, isDemo]);

  // Expand all nodes by default
  const getAllExpandedState = React.useCallback(() => {
    const expanded = {};
    function expandAll(nodeId) {
      expanded[nodeId] = true;
      (tree[nodeId].children || []).forEach(expandAll);
    }
    expandAll(ROOT_ID);
    return expanded;
  }, [tree]);

  // Custom layout direction: top-to-bottom (TB)
  const customLayout = React.useCallback((visNodes, visEdges) => {
    const dagre = require("dagre");
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    const W = 180,
      H = 60;
    const dynamicNodesep = Math.max(10, visNodes.length * 5);
    const dynamicRanksep = Math.max(800, visNodes.length * 7);
    g.setGraph({
      rankdir: "TB", // Top-to-bottom
      nodesep: dynamicNodesep / 1.25,
      ranksep: dynamicRanksep,
    });
    visNodes.forEach((n) => g.setNode(n.id, { width: W, height: H }));
    visEdges.forEach((e) => g.setEdge(e.source, e.target));
    dagre.layout(g);
    return Object.fromEntries(
      visNodes.map((n) => {
        const { x, y } = g.node(n.id);
        return [n.id, { x: x - W / 2, y: y - H / 2 }];
      })
    );
  }, []);

  const handleNodeHeaderClick = useCallback(
    (nodeId) => {
      // Find the node data from the TREE
      const nodeData = tree[nodeId];
      if (nodeData) {
        setSelectedNode({
          id: nodeId,
          ...nodeData,
          onDeadlineChange: (newDeadline) => {
            // Update the TREE data with the new deadline
            setTree((prevTree) => ({
              ...prevTree,
              [nodeId]: { ...prevTree[nodeId], deadline: newDeadline },
            }));
            // Update the selected node state
            setSelectedNode((prev) => ({ ...prev, deadline: newDeadline }));
          },
          onTodoToggle: (todoId, updatedTodos) => {
            // If updatedTodos is provided, use that; otherwise toggle the specific todo
            if (updatedTodos) {
              // Update the entire todos array
              setTree((prevTree) => {
                // Update selectedNode immediately with the new todos
                setSelectedNode((prev) => ({
                  ...prev,
                  todos: updatedTodos,
                }));
                return {
                  ...prevTree,
                  [nodeId]: { ...prevTree[nodeId], todos: updatedTodos },
                };
              });
            } else {
              // Find and toggle the todo in the TREE data
              setTree((prevTree) => {
                const updatedTodos = prevTree[nodeId].todos.map((t) =>
                  t.id === todoId ? { ...t, done: !t.done } : t
                );
                // Update selectedNode immediately with the new todos
                setSelectedNode((prev) => ({
                  ...prev,
                  todos: updatedTodos,
                }));
                return {
                  ...prevTree,
                  [nodeId]: { ...prevTree[nodeId], todos: updatedTodos },
                };
              });
            }
          },
          onTodoAdd: (newTodo) => {
            // Add the new todo to the TREE data
            setTree((prevTree) => {
              const updatedTodos = [...(prevTree[nodeId].todos || []), newTodo];
              // Update selectedNode immediately with the new todos
              setSelectedNode((prev) => ({
                ...prev,
                todos: updatedTodos,
              }));
              return {
                ...prevTree,
                [nodeId]: { ...prevTree[nodeId], todos: updatedTodos },
              };
            });
          },
          onTodoRemove: (removedTodoId, updatedTodos) => {
            // Remove the todo from the TREE data
            setTree((prevTree) => {
              // Update selectedNode immediately with the new todos
              setSelectedNode((prev) => ({
                ...prev,
                todos: updatedTodos,
              }));
              return {
                ...prevTree,
                [nodeId]: { ...prevTree[nodeId], todos: updatedTodos },
              };
            });
          },
          onNotesChange: (newNotes) => {
            // Update the TREE data with the new notes
            setTree((prevTree) => ({
              ...prevTree,
              [nodeId]: { ...prevTree[nodeId], notes: newNotes },
            }));
            // Update the selected node state
            setSelectedNode((prev) => ({ ...prev, notes: newNotes }));
          },
          onStatusChange: (newStatus) => {
            setTree((prevTree) => {
              const updatedNode = { ...prevTree[nodeId], status: newStatus };
              return { ...prevTree, [nodeId]: updatedNode };
            });
            setSelectedNode((prev) => ({ ...prev, status: newStatus }));
          },
        });
        setSidebarOpen(true);
      }
    },
    [tree]
  );

  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false);
    setSelectedNode(null);
  }, []);

  // Use the custom expanded state and layout
  const {
    nodes: treeNodes,
    edges,
    freezePosition,
    revision,
  } = useCreditPlanFlowNodes(
    tree,
    ROOT_ID,
    handleNodeHeaderClick,
    "TB",
    selectedNode?.id
  );

  const [nodes, setNodes] = useState(treeNodes);

  React.useEffect(() => {
    setNodes(treeNodes);
  }, [treeNodes]);

  const onConnect = useCallback(
    (params) => rfInstance && rfInstance.addEdges(addEdge(params, edges)),
    [rfInstance, edges]
  );

  // Only fitView on initial load or when tree structure actually changes (not on selection changes)
  useLayoutEffect(() => {
    if (rfInstance && tree && Object.keys(tree).length > 0) {
      const currentTreeKeys = Object.keys(tree).sort().join(",");
      const treeStructureChanged = previousTreeKeys.current !== currentTreeKeys;

      // Only fitView if:
      // 1. We haven't done initial fitView yet, OR
      // 2. The tree structure actually changed (new nodes added/removed)
      if (!hasInitialFitView.current || treeStructureChanged) {
        previousTreeKeys.current = currentTreeKeys;
        hasInitialFitView.current = true;

        const id = setTimeout(() => {
          // Increase padding to zoom out more (0.3 = 30% padding on all sides)
          rfInstance.fitView({ padding: 0.3, duration: 300, minZoom: 0.1 }); // 300 ms smooth zoom
        }, 100);

        return () => {
          clearTimeout(id);
        };
      }
    }
  }, [rfInstance, tree]); // Removed revision dependency - only depend on tree structure

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (!rfInstance) return;
    const type = e.dataTransfer.getData("application/reactflow");
    if (!type) return;

    const bounds = wrapperRef.current.getBoundingClientRect();
    const position = rfInstance.project({
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top,
    });

    // For now, we'll skip adding new nodes from sidebar to keep it simple
    console.log("Dropped node type:", type, "at position:", position);
  };

  if (loading || regeneratingPlan) {
    return (
      <div className="credit-plan-flow loading">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <h2>
            {regeneratingPlan
              ? t("creditPlanFlow.regeneratingPlan")
              : t("creditPlanFlow.loadingPlan")}
          </h2>
          <p>
            {regeneratingPlan
              ? t("creditPlanFlow.regeneratingMessage")
              : t("creditPlanFlow.loadingMessage")}
          </p>
        </div>
      </div>
    );
  }

  // Debug: Log tree state
  console.log("Current tree state:", tree);
  console.log("Tree keys:", tree ? Object.keys(tree) : "no tree");

  // Show empty state if no tree data is available
  if (!tree || Object.keys(tree).length === 0) {
    return (
      <div className="credit-plan-flow empty">
        <div className="empty-content">
          <h2>{t("creditPlanFlow.noPlanAvailable")}</h2>
          <p>{t("creditPlanFlow.noPlanMessage")}</p>
          <button
            onClick={() => (window.location.href = "/actionable")}
            className="btn-primary"
          >
            {t("creditPlanFlow.createAssessment")}
          </button>
        </div>
      </div>
    );
  }

  // Show empty state if no nodes are available (tree exists but no valid nodes)
  if (!nodes || nodes.length === 0) {
    return (
      <div className="credit-plan-flow empty">
        <div className="empty-content">
          <h2>{t("creditPlanFlow.processingPlan")}</h2>
          <p>{t("creditPlanFlow.processingMessage")}</p>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dndflow-plan-flow">
      {/* Header with assessment info */}
      {currentAssessmentData && (
        <div className="flow-header">
          <div className="header-content">
            <h1>{t("creditPlanFlow.title")}</h1>
            <ProgressIndicator tree={tree} />
            <div className="assessment-info">
              <span className="goal-info">
                {t("creditPlanFlow.goalLabel")}{" "}
                {currentAssessmentData.goal?.goal}
              </span>
              <span className="timeline-info">
                {t("creditPlanFlow.timelineLabel")}{" "}
                {currentAssessmentData.goal?.timeline}
              </span>
            </div>
            <div className="header-actions">
              <button
                className="ai-preview-btn"
                onClick={() => {
                  console.log(
                    "Current assessment data:",
                    currentAssessmentData
                  );
                  if (currentAssessmentData) {
                    setShowAIPreview(true);
                  } else {
                    console.warn("No assessment data available for AI preview");
                  }
                }}
                title={t("creditPlanFlow.viewAIPreview")}
                disabled={!currentAssessmentData}
              >
                {t("creditPlanFlow.aiAnalysisPreview")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReactFlowProvider>
        <div
          className="reactflow-wrapper"
          ref={wrapperRef}
          style={{
            height: assessmentData
              ? "calc(100vh - 280px)"
              : "calc(100vh - 230px)",
            width: "100%",
          }}
        >
          <ReactFlowErrorBoundary>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onInit={setRfInstance}
              onNodeClick={(_, node) => handleNodeHeaderClick(node.id)}
              onNodesChange={debounce((changes) => {
                setNodes((nds) => applyNodeChanges(changes, nds));
                changes.forEach((change) => {
                  if (change.type === "position" && change.position) {
                    freezePosition(change.id, change.position);
                  }
                });
              }, 50)}
              onNodeDragStop={(_, node) =>
                freezePosition(node.id, node.position)
              }
              onConnect={onConnect}
              onDragOver={onDragOver}
              onDrop={onDrop}
              fitView
              fitViewOptions={{ padding: 0.25, minZoom: 0.1 }}
              minZoom={0.1}
              maxZoom={2}
              nodesDraggable={false}
            >
              <Controls />
            </ReactFlow>
          </ReactFlowErrorBoundary>
        </div>

        {/* Right Panel */}
        <div
          className={`right-panel ${sidebarOpen ? "open" : "closed"}${
            isFullscreen ? " fullscreen" : ""
          }`}
        >
          <div className="right-panel-content">
            {console.log("Rendering RightPanelRenderer with:", {
              currentPlanId,
              selectedNodeId: selectedNode?.id,
              sidebarOpen,
              hasSelectedNode: !!selectedNode,
            })}
            <RightPanelRenderer
              node={selectedNode}
              planId={currentPlanId}
              onClose={handleCloseSidebar}
              onFullscreenChange={setIsFullscreen}
              isDemo={isDemo}
              // Pass context for lazy loading step expansion
              expansionContext={
                currentAssessmentData
                  ? {
                      goal: currentAssessmentData.goal,
                      formData:
                        currentAssessmentData.formData ||
                        currentAssessmentData.creditPlan?.formData,
                      subcategory: currentAssessmentData.subcategory,
                      planStructure: tree,
                    }
                  : null
              }
              // Handle step expansion updates
              onStepExpanded={(stepId, expandedStepData) => {
                console.log("Step expanded:", stepId, expandedStepData);
                // Update the tree with expanded step data
                setTree((prevTree) => ({
                  ...prevTree,
                  [stepId]: expandedStepData,
                }));
                // Update selected node if it's the same step
                if (selectedNode?.id === stepId) {
                  setSelectedNode((prev) => ({
                    ...prev,
                    ...expandedStepData,
                  }));
                }
              }}
            />
          </div>
        </div>
      </ReactFlowProvider>

      {/* AI Analysis Preview Modal */}
      {showAIPreview && currentAssessmentData && (
        <div
          className="ai-preview-modal-overlay"
          onClick={() => setShowAIPreview(false)}
        >
          <div
            className="ai-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ai-preview-header">
              <h2>{t("creditPlanFlow.aiAnalysisPreview")}</h2>
              <button
                className="close-btn"
                onClick={() => setShowAIPreview(false)}
              >
                ×
              </button>
            </div>
            <div className="ai-preview-content">
              {currentAssessmentData.aiAnalysis ? (
                <div className="ai-analysis-content">
                  <MarkdownRenderer
                    content={currentAssessmentData.aiAnalysis}
                    showFullText={true}
                    maxLength={null}
                    onToggleText={() => {}}
                    showToggleButton={false}
                  />
                </div>
              ) : (
                <div className="no-ai-analysis">
                  <p>{t("creditPlanFlow.noAIAnalysis")}</p>
                  <p>{t("creditPlanFlow.completeAssessmentMessage")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
